// import { RoutePath } from '@/config';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, SubmitHandler } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

// import { useNavigate } from 'react-router-dom';
import { AuthApi } from '../../api';
import { ClientLoginFormFields, useClientLoginForm } from '../../hooks';
import { normalizePhoneNumber } from '../../utils';

interface Props {
  onSuccess(): void;
}

export const ClientLoginFormFirstStep: FC<Props> = ({ onSuccess }) => {
  const phoneInputRef = useRef<HTMLInputElement>(null);
  // const navigate = useNavigate();

  useEffect(() => {
    phoneInputRef.current?.focus();
  }, []);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useClientLoginForm();
  const phone = watch('phoneNumber') ?? '';
  const onSubmit: SubmitHandler<ClientLoginFormFields> = useCallback(
    async ({ phoneNumber }) => {
      await AuthApi.sendCode(normalizePhoneNumber(phoneNumber));
      onSuccess();
    },
    [onSuccess],
  );

  const isPhoneValid = useMemo(
    () => phone.replace(/\D/g, '').length === 11,
    [phone],
  );

  // Обработка клавиатуры: Enter для продолжения
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        event.preventDefault();
        if (isPhoneValid && !isSubmitting) {
          void handleSubmit(onSubmit)();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPhoneValid, isSubmitting, handleSubmit, onSubmit]);

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="m-auto flex w-full flex-col items-center md:w-[720px]"
      >
        <div className="flex w-full flex-col items-center rounded-[24px] bg-white px-[24px] py-8 lg:px-12">
          <div className="mb-[32px] flex w-full flex-col items-center gap-[16px]">
            <h1 className="text-center text-[30px] font-medium leading-[36px] text-[#151515]">
              Введите номер телефона
            </h1>
            <p className="text-center text-[16px] font-normal leading-[20px] text-[#151515] opacity-50">
              Вам придет код подтверждения
            </p>
          </div>

          <div className="mb-[32px] flex w-full items-center rounded-[16px] border border-[#afc4d1]">
            <Controller
              control={control}
              name="phoneNumber"
              rules={{
                required: true,
              }}
              render={({ field }) => {
                return (
                  <IMaskInput
                    inputRef={phoneInputRef}
                    mask="+7 (000) 000 00-00"
                    lazy={false}
                    overwrite={false}
                    name={field.name}
                    value={field.value ?? ''}
                    onBlur={field.onBlur}
                    onAccept={(value: string) => field.onChange(value)}
                    inputMode="tel"
                    type="tel"
                    className="w-full rounded-[16px] p-[18px] text-base font-normal leading-5 text-[#151515] outline-none placeholder:text-[#151515]/50"
                    placeholder="+7 (___) ___ __-__"
                  />
                );
              }}
            />
          </div>
          <button
            type="button"
            disabled={!isPhoneValid || isSubmitting}
            onClick={handleSubmit(onSubmit)}
            className="flex h-[60px] w-full items-center justify-center rounded-[16px] bg-[#0788f1] p-[15px] text-base font-bold text-white transition-opacity disabled:opacity-50"
          >
            {isSubmitting ? 'Отправка...' : 'Продолжить'}
          </button>
          {/* <button
            type="button"
            className="button--secondary hidden button button--block mt-[12px]"
            onClick={() => navigate(RoutePath.ClientRegister)}
          >
            Зарегистрироваться
          </button> */}
        </div>
      </form>
    </>
  );
};
